import { useEventLogging } from '@AppModels/Logging';
import { Industry } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { ISurveyTemplateCard, TemplateStatus } from '../../../../extViewmodels/sdk/models/SurveyTemplate';
import { getIndustryName } from '../../../../models/UiUtils';
import {
	useCloneSurveyTemplateMutation,
	useDeleteSurveyTemplateMutation,
	usePublishSurveyTemplateMutation,
	useSurveyTemplatesByIndustryQuery,
} from '../../../../queries/SurveyTemplate';
import { DeleteConfirmation } from '../../../../web/components/DeleteConfirmation';
import { SurveyTemplatesTable } from '../../../components/templates/SurveyTemplatesTable';
import { DuplicateTemplateModal } from '../../templates/SurveyTemplates/DuplicateTemplateModal';
import { styleSheet } from './styles';

interface IProps {
	industry: Industry;
}

export function SurveyTemplatesByIndustry({ industry }: IProps) {
	const { logEvent } = useEventLogging();
	const history = useHistory();

	// Modal states
	const [deleteModalState, setDeleteModalState] = React.useState<{
		isOpen: boolean;
		template: ISurveyTemplateCard | null;
	}>({
		isOpen: false,
		template: null,
	});

	const [duplicateModalState, setDuplicateModalState] = React.useState<{
		isOpen: boolean;
		template: ISurveyTemplateCard | null;
	}>({
		isOpen: false,
		template: null,
	});

	// Track which template is being mutated for loading indicators
	const [mutatingTemplateId, setMutatingTemplateId] = React.useState<string | null>(null);

	// Data fetching - Admin endpoint to see all templates including drafts
	const { data, isLoading, error, refetch } = useSurveyTemplatesByIndustryQuery({ industry });

	// Admin mutations
	const deleteMutation = useDeleteSurveyTemplateMutation({
		onSuccess: () => {
			toast.success('Template deleted successfully');
			refetch();
		},
		onError: () => {
			toast.error('Failed to delete template');
		},
	});

	const publishMutation = usePublishSurveyTemplateMutation({
		onSuccess: () => {
			toast.success('Template published successfully');
			setMutatingTemplateId(null);
			refetch();
		},
		onError: () => {
			toast.error('Failed to publish template');
			setMutatingTemplateId(null);
		},
	});

	const cloneMutation = useCloneSurveyTemplateMutation({
		onSuccess: () => {
			toast.success('Template duplicated successfully');
			setDuplicateModalState({ isOpen: false, template: null });
			refetch();
		},
		onError: () => {
			toast.error('Failed to duplicate template');
			setDuplicateModalState({ isOpen: false, template: null });
		},
	});

	// Helper to map string enum values from API to numeric enum values used in frontend
	const mapStatusToNumericEnum = (status: any): TemplateStatus | undefined => {
		if (status === 'Draft' || status === TemplateStatus.Draft) return TemplateStatus.Draft;
		if (status === 'Published' || status === TemplateStatus.Published) return TemplateStatus.Published;
		if (status === 'Archived' || status === TemplateStatus.Archived) return TemplateStatus.Archived;
		if (status === 'Disabled' || status === TemplateStatus.Disabled) return TemplateStatus.Disabled;
		return undefined;
	};

	// Filter templates by status (3 separate sections to match automations)
	// Note: ISystemSurveyTemplate has industries mapping, we need to check the status for this specific industry
	// and convert to ISurveyTemplateCard for the table
	const templates = React.useMemo(
		() =>
			data?.map(systemTemplate => ({
				_type: 'SurveyTemplateCard' as const,
				id: systemTemplate.template.id,
				name: systemTemplate.template.name,
				summary: systemTemplate.template.summary,
				keywords: systemTemplate.template.keywords,
				scope: systemTemplate.template.scope,
				status: mapStatusToNumericEnum(systemTemplate.industries[industry]),
				creationDate: systemTemplate.template.creationDate,
				lastModifiedDate: systemTemplate.template.lastModifiedDate,
				industries: Object.keys(systemTemplate.industries).filter(
					ind => systemTemplate.industries[ind] !== undefined && systemTemplate.industries[ind] !== null
				),
			})) || [],
		[data, industry]
	);

	const drafts = React.useMemo(() => templates.filter(t => t.status === TemplateStatus.Draft), [templates]);
	const published = React.useMemo(() => templates.filter(t => t.status === TemplateStatus.Published), [templates]);
	const archived = React.useMemo(() => templates.filter(t => t.status === TemplateStatus.Archived), [templates]);

	// Action handlers
	const handleTemplateClicked = React.useCallback(
		(template: ISurveyTemplateCard) => {
			logEvent('ViewSurveyTemplate', { templateId: template.id, industry });
			history.push(`/surveys/template/custom/${template.id}/edit`);
		},
		[industry, logEvent, history]
	);

	const handleEditTemplate = React.useCallback(
		(template: ISurveyTemplateCard) => {
			logEvent('EditSurveyTemplate', { templateId: template.id, industry });
			history.push(`/surveys/template/custom/${template.id}/edit`);
		},
		[industry, logEvent, history]
	);

	const handleDuplicateTemplate = React.useCallback(
		(template: ISurveyTemplateCard) => {
			logEvent('DuplicateSurveyTemplate', { templateId: template.id, industry });
			setDuplicateModalState({ isOpen: true, template });
		},
		[industry, logEvent]
	);

	const handleDuplicateConfirm = React.useCallback(
		async (newName: string) => {
			const template = duplicateModalState.template;
			if (!template) return;

			try {
				await cloneMutation.mutateAsync({
					id: template.id,
					name: newName,
				});
			} catch (err) {
				console.error('Failed to duplicate template:', err);
			}
		},
		[duplicateModalState.template, cloneMutation]
	);

	const handlePublishTemplate = React.useCallback(
		async (template: ISurveyTemplateCard) => {
			logEvent('PublishSurveyTemplate', { templateId: template.id, industry });
			setMutatingTemplateId(template.id);

			try {
				await publishMutation.mutateAsync({
					id: template.id,
					industry,
				});
			} catch (err) {
				console.error('Failed to publish template:', err);
			}
		},
		[industry, logEvent, publishMutation]
	);

	const handleDeleteTemplate = React.useCallback(
		(template: ISurveyTemplateCard) => {
			logEvent('DeleteSurveyTemplate', { templateId: template.id, industry });
			setDeleteModalState({ isOpen: true, template });
		},
		[industry, logEvent]
	);

	const handleDeleteConfirm = React.useCallback(
		async (shouldDelete: boolean) => {
			const template = deleteModalState.template;
			setDeleteModalState({ isOpen: false, template: null });

			if (shouldDelete && template) {
				try {
					await deleteMutation.mutateAsync({ id: template.id, industry });
				} catch (err) {
					console.error('Failed to delete template:', err);
				}
			}
		},
		[deleteModalState.template, deleteMutation, industry]
	);

	// Error state
	if (error) {
		return (
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.errorBanner)}>
					<strong>Error loading survey templates</strong>
					<p>Failed to load templates for {getIndustryName(industry)}. Please try again.</p>
					<button onClick={() => refetch()}>Retry</button>
				</div>
			</div>
		);
	}

	// Main render with three sections to match automations
	return (
		<>
			<div className={css(styleSheet.container)}>
				{/* Page Header */}
				<div className={css(styleSheet.header)}>
					<h1 className={css(styleSheet.headerTitle)}>{getIndustryName(industry)}</h1>
				</div>

				<div className={css(styleSheet.body)}>
					{/* Not Yet Published Section (Drafts) */}

					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionTitle, styleSheet.draftsSectionTitle)}>Not Yet Published</div>
						<SurveyTemplatesTable
							templates={drafts}
							isLoading={isLoading}
							mutatingTemplateId={mutatingTemplateId}
							nameColumnLabel='Survey Template Name'
							onTemplateClicked={handleTemplateClicked}
							onEditTemplate={handleEditTemplate}
							onPublishTemplate={handlePublishTemplate}
							onDeleteTemplate={handleDeleteTemplate}
						/>
					</div>

					{/* Live Survey Templates Section (Published) */}
					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionTitle)}>Live Survey Templates</div>
						<SurveyTemplatesTable
							templates={published}
							isLoading={isLoading}
							mutatingTemplateId={mutatingTemplateId}
							nameColumnLabel='Survey Template Name'
							onTemplateClicked={handleTemplateClicked}
							onEditTemplate={handleEditTemplate}
							onDuplicateTemplate={handleDuplicateTemplate}
							onDeleteTemplate={handleDeleteTemplate}
						/>
					</div>

					{/* Archived Survey Templates Section */}
					<div className={css(styleSheet.section)}>
						<div className={css(styleSheet.sectionTitle)}>Archived Survey Templates</div>
						<SurveyTemplatesTable
							templates={archived}
							isLoading={isLoading}
							mutatingTemplateId={mutatingTemplateId}
							nameColumnLabel='Survey Template Name'
							onTemplateClicked={handleTemplateClicked}
							onPublishTemplate={handlePublishTemplate}
							onDeleteTemplate={handleDeleteTemplate}
						/>
					</div>
				</div>
			</div>

			{/* Delete Confirmation Modal */}
			{deleteModalState.isOpen && deleteModalState.template && (
				<DeleteConfirmation
					isOpen={deleteModalState.isOpen}
					onFinish={handleDeleteConfirm}
					showSpinner={deleteMutation.isPending}
					title='Delete Survey Template'
					bodyText={`Are you sure you want to delete "${deleteModalState.template.name}"? This action cannot be undone.`}
					destructiveButtonText='Delete'
				/>
			)}

			{/* Duplicate Template Modal */}
			{duplicateModalState.isOpen && duplicateModalState.template && (
				<DuplicateTemplateModal
					open={duplicateModalState.isOpen}
					onOpenChange={isOpen => setDuplicateModalState({ isOpen, template: duplicateModalState.template })}
					templateName={duplicateModalState.template.name}
					onConfirm={handleDuplicateConfirm}
					isLoading={cloneMutation.isPending}
				/>
			)}
		</>
	);
}
